import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";

type PendingNavigation = { type: "navigate"; to: string } | { type: "back" };

/**
 * Warns the user before leaving a form that has unsaved changes.
 *
 * Covers the three ways a user can leave a form:
 *  - the browser (refresh / closing the tab) via `beforeunload`
 *  - in-app links (sidebar, menus, breadcrumbs) via a capture phase click listener
 *  - the browser back button via an extra history entry that is re-pushed until confirmed
 *
 * Programmatic navigation done by the form itself (e.g. after a successful save)
 * is not guarded - use `guardedNavigate` for the cancel/leave buttons instead.
 */
export const useUnsavedChanges = (hasUnsavedChanges: boolean) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingNavigation | null>(null);
  const [leaving, setLeaving] = useState<PendingNavigation | null>(null);
  const isHistoryGuardArmed = useRef(false);

  const isGuardActive = hasUnsavedChanges && !leaving;

  // Refresh / close tab
  useEffect(() => {
    if (!isGuardActive) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isGuardActive]);

  // In-app links
  useEffect(() => {
    if (!isGuardActive) return;
    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("javascript:") ||
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      const to = `${url.pathname}${url.search}${url.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (to === current) return;

      event.preventDefault();
      event.stopPropagation();
      setPending({ type: "navigate", to });
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [isGuardActive]);

  // Browser back button
  useEffect(() => {
    if (!isGuardActive) return;
    if (!isHistoryGuardArmed.current) {
      // Duplicate the current entry so "back" lands on the same URL instead of leaving.
      window.history.pushState(window.history.state, "", window.location.href);
      isHistoryGuardArmed.current = true;
    }
    const handlePopState = () => {
      // Re-arm the guard so the user stays on the form until they confirm.
      window.history.pushState(window.history.state, "", window.location.href);
      setPending({ type: "back" });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isGuardActive]);

  // Runs after the guards above have been detached, so leaving is not re-blocked.
  useEffect(() => {
    if (!leaving) return;
    if (leaving.type === "back") {
      // Skip the form entry and the duplicate we pushed on top of it.
      window.history.go(-2);
    } else {
      // Replacing drops the duplicate entry the guard pushed, so the back
      // button keeps working normally on the page we are leaving to.
      navigate(leaving.to, { replace: isHistoryGuardArmed.current });
    }
  }, [leaving, navigate]);

  const guardedNavigate = useCallback(
    (to: string) => {
      if (!hasUnsavedChanges || leaving) {
        navigate(to);
        return;
      }
      setPending({ type: "navigate", to });
    },
    [hasUnsavedChanges, leaving, navigate],
  );

  const handleStay = useCallback(() => setPending(null), []);

  const handleLeave = useCallback(() => {
    setPending(null);
    setLeaving(pending);
  }, [pending]);

  const unsavedChangesModal = (
    <Modal
      show={!!pending}
      onHide={handleStay}
      className="fade zoomIn"
      dialogClassName="modal-dialog-centered"
    >
      <Modal.Header closeButton style={{ borderBottom: "none" }}></Modal.Header>
      <Modal.Body>
        <div className="mt-2 text-center">
          <i className="ti ti-alert-triangle fs-1 text-warning"></i>
          <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
            <h5>{t("unsavedChanges.title")}</h5>
            <p className="text-muted mx-4 mb-0">{t("unsavedChanges.message")}</p>
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <Button
            type="button"
            variant="light"
            className="btn w-sm"
            onClick={handleStay}
          >
            {t("unsavedChanges.stay")}
          </Button>
          <Button
            type="button"
            variant="danger"
            className="btn w-sm"
            onClick={handleLeave}
          >
            {t("unsavedChanges.leave")}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );

  return { guardedNavigate, unsavedChangesModal };
};
